const DEV_VERSION = "dev";
const VERSION_ENDPOINT = "/api/version";
const MIN_CHECK_INTERVAL_MS = 60_000;
const RESHOW_AFTER_HIDDEN_MS = 60 * 60_000;
const CHUNK_RELOAD_COOLDOWN_MS = 60_000;
const CHUNK_RELOAD_STORAGE_KEY = "jst-chunk-reload-at";
const CHUNK_LOAD_FAILURE_MESSAGE = /Loading (CSS )?chunk \S+ failed/i;

export const APP_VERSION = process.env.NEXT_PUBLIC_APP_VERSION;

export function isVersionCheckEnabled(version: unknown): version is string {
  return typeof version === "string" && version !== "" && version !== DEV_VERSION;
}

export function isDifferentVersion(bundleVersion: string, remoteVersion: unknown): boolean {
  return isVersionCheckEnabled(remoteVersion) && remoteVersion !== bundleVersion;
}

interface VersionCheckerOptions {
  bundleVersion: string;
  onNewVersion: () => void;
  fetchFn?: typeof fetch;
  now?: () => number;
}

export function createVersionChecker({
  bundleVersion,
  onNewVersion,
  fetchFn = (input, init) => fetch(input, init),
  now = Date.now,
}: VersionCheckerOptions) {
  let lastCheckAt = -Infinity;
  let newVersionFound = false;

  async function fetchRemoteVersion(): Promise<unknown> {
    try {
      const response = await fetchFn(VERSION_ENDPOINT, { cache: "no-store" });
      if (!response.ok) return undefined;
      const body = await response.json();
      return body?.version;
    } catch {
      return undefined;
    }
  }

  return {
    async check() {
      if (newVersionFound || now() - lastCheckAt < MIN_CHECK_INTERVAL_MS) return;
      lastCheckAt = now();

      const remoteVersion = await fetchRemoteVersion();
      if (newVersionFound || !isDifferentVersion(bundleVersion, remoteVersion)) return;
      newVersionFound = true;
      onNewVersion();
    },
  };
}

export function shouldReshow(hiddenAt: number | null, now: number): boolean {
  return hiddenAt !== null && now - hiddenAt >= RESHOW_AFTER_HIDDEN_MS;
}

export function isChunkLoadError(errorOrReason: unknown): boolean {
  if (typeof errorOrReason === "string") {
    return CHUNK_LOAD_FAILURE_MESSAGE.test(errorOrReason);
  }
  if (!(errorOrReason instanceof Error)) return false;
  return (
    errorOrReason.name === "ChunkLoadError" ||
    CHUNK_LOAD_FAILURE_MESSAGE.test(errorOrReason.message)
  );
}

export function claimChunkReload(
  storage: Pick<Storage, "getItem" | "setItem">,
  now: number,
): boolean {
  try {
    const lastReloadAt = Number(storage.getItem(CHUNK_RELOAD_STORAGE_KEY));
    if (now - lastReloadAt < CHUNK_RELOAD_COOLDOWN_MS) return false;
    storage.setItem(CHUNK_RELOAD_STORAGE_KEY, String(now));
    return true;
  } catch {
    return false;
  }
}
